<template>
  <div class="app-shell">
    <div class="nebula-bg">
      <div class="nebula-orb nebula-orb--1"></div>
      <div class="nebula-orb nebula-orb--2"></div>
      <div class="nebula-orb nebula-orb--3"></div>
      <div class="stars"></div>
    </div>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
  </div>
</template>

<script setup>
import { watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSessionStore } from '@/store/session'

const router = useRouter()
const route = useRoute()
const session = useSessionStore()

let idleTimer = null

function resetIdleTimer() {
  clearTimeout(idleTimer)
  if (route.path === '/') return
  idleTimer = setTimeout(() => {
    session.reset()
    router.push('/')
  }, 90_000)
}

document.addEventListener('touchstart', resetIdleTimer, { passive: true })
document.addEventListener('click', resetIdleTimer, { passive: true })
document.addEventListener('mousemove', resetIdleTimer, { passive: true })

watch(() => route.path, resetIdleTimer, { immediate: true })
</script>

<style>
.app-shell {
  position: fixed;
  inset: 0;
  overflow: hidden;
}

.nebula-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: #050510;
}

.nebula-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
}

.nebula-orb--1 {
  width: 900px;
  height: 900px;
  background: radial-gradient(circle, rgba(109, 40, 217, 0.25) 0%, transparent 70%);
  top: -300px;
  left: -200px;
  animation: nebulaDrift1 30s ease-in-out infinite;
}

.nebula-orb--2 {
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.2) 0%, transparent 70%);
  bottom: -200px;
  right: -150px;
  animation: nebulaDrift2 25s ease-in-out infinite reverse;
}

.nebula-orb--3 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%);
  top: 40%;
  left: 40%;
  transform: translate(-50%, -50%);
  animation: nebulaDrift3 35s ease-in-out infinite;
}

.stars {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 100%),
    radial-gradient(1px 1px at 80% 10%, rgba(255,255,255,0.2) 0%, transparent 100%),
    radial-gradient(1px 1px at 60% 70%, rgba(255,255,255,0.25) 0%, transparent 100%),
    radial-gradient(1px 1px at 10% 80%, rgba(255,255,255,0.15) 0%, transparent 100%),
    radial-gradient(1px 1px at 90% 50%, rgba(255,255,255,0.2) 0%, transparent 100%),
    radial-gradient(1.5px 1.5px at 45% 20%, rgba(255,255,255,0.3) 0%, transparent 100%),
    radial-gradient(1px 1px at 70% 90%, rgba(255,255,255,0.2) 0%, transparent 100%),
    radial-gradient(1.5px 1.5px at 30% 60%, rgba(255,255,255,0.25) 0%, transparent 100%);
}

@keyframes nebulaDrift1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(80px, 40px) scale(1.05); }
  66% { transform: translate(-40px, 80px) scale(0.95); }
}

@keyframes nebulaDrift2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-60px, -30px) scale(1.08); }
  66% { transform: translate(50px, -60px) scale(0.92); }
}

@keyframes nebulaDrift3 {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.3); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ─── Global view styles ─── */
.view {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.orbitron { font-family: 'Orbitron', sans-serif; }

.neon-text {
  text-shadow: 0 0 20px currentColor, 0 0 40px currentColor;
}

.glass-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
}

.neon-btn {
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #fff;
  background: transparent;
  border: 2px solid rgba(139, 92, 246, 0.8);
  border-radius: 8px;
  padding: 1rem 3rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px rgba(139, 92, 246, 0.3), inset 0 0 20px rgba(139, 92, 246, 0.05);
}

.neon-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.neon-btn:active::before { opacity: 1; }
.neon-btn:active {
  box-shadow: 0 0 40px rgba(139, 92, 246, 0.6), inset 0 0 30px rgba(139, 92, 246, 0.2);
  transform: scale(0.98);
}
</style>
